import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import EmailChatPanel from "@/components/EmailChatPanel";
import { 
  Inbox, Trash2, Search, RefreshCw, MailOpen, Mail, MessageSquare, CheckCircle, CheckSquare, Tag, User, Settings
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIActorEmails() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  
  const [actor, setActor] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loadingActor, setLoadingActor] = useState(true);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailSearch, setEmailSearch] = useState("");
  const [inboxSearch, setInboxSearch] = useState(""); // Separate search for inbox
  const [archivedSearch, setArchivedSearch] = useState(""); // Separate search for archived
  const [emailsAccountCount, setEmailsAccountCount] = useState(0);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalEmails, setTotalEmails] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const EMAILS_PER_PAGE = 50;
  
  const [chatOpen, setChatOpen] = useState(false);
  const [viewType, setViewType] = useState("inbox"); // "inbox", "archived", or "sent"
  const [archivedEmails, setArchivedEmails] = useState([]);
  const [totalArchivedEmails, setTotalArchivedEmails] = useState(0);
  const [sentEmails, setSentEmails] = useState([]);
  const [totalSentEmails, setTotalSentEmails] = useState(0);
  const [sentSearch, setSentSearch] = useState("");
  
  const [trainingStatus, setTrainingStatus] = useState({
    status: "not_started",
    training_progress: 0,
    total_emails: 0,
    last_analyzed: null
  });
  
  const [analyzingEmails, setAnalyzingEmails] = useState(false);
  
  // Bulk selection state
  const [selectedEmailIds, setSelectedEmailIds] = useState(new Set());
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [deletingBulk, setDeletingBulk] = useState(false);

  useEffect(() => {
    fetchActorDetails();
    fetchTrainingStatus();
    fetchArchivedCount(); // Fetch archived count on initial load
    fetchSentCount(); // Fetch sent count on initial load
    fetchAvailableFilters(); // Fetch available categories and tags
    if (viewType === "inbox") {
      fetchEmails(inboxSearch, 1);
      setEmailSearch(inboxSearch); // Sync the main search with current tab
    } else if (viewType === "archived") {
      fetchArchivedEmails(archivedSearch, 1);
      setEmailSearch(archivedSearch); // Sync the main search with current tab
    } else if (viewType === "sent") {
      fetchSentEmails(sentSearch, 1);
      setEmailSearch(sentSearch); // Sync the main search with current tab
    }
  }, [actorId, viewType]); // Depend on actorId and viewType
  
  // Auto-refresh training status during training
  useEffect(() => {
    let interval;
    if (trainingStatus.status === "in_progress") {
      interval = setInterval(() => {
        fetchTrainingStatus();
      }, 2000); // Poll every 2 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [trainingStatus.status]);
  
  // Separate effect for polling that doesn't interfere with pagination
  useEffect(() => {
    // Smart polling: Check for new emails every 30 seconds WITHOUT refreshing the page
    const pollInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem("token");
        
        // Check inbox count
        const inboxResponse = await axios.get(
          `${API}/admin/actors/${actorId}/emails?limit=1&skip=0`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update email accounts status (sync status) without disrupting
        setEmailAccounts(inboxResponse.data.email_accounts || []);
        
        // Check if any account is currently syncing
        const isSyncing = inboxResponse.data.email_accounts?.some(acc => acc.is_syncing);
        
        // Only update if new emails arrived AND not currently syncing
        if (inboxResponse.data.total !== totalEmails && totalEmails > 0 && !isSyncing) {
          const newEmailCount = inboxResponse.data.total - totalEmails;
          
          if (viewType === "inbox" && page === 1 && !loadingEmails) {
            // User is on inbox first page - silently fetch new emails without showing loading
            try {
              const searchParam = inboxSearch ? `&search=${encodeURIComponent(inboxSearch)}` : "";
              const response = await axios.get(
                `${API}/admin/actors/${actorId}/emails?limit=${EMAILS_PER_PAGE}&skip=0${searchParam}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              // Update state without triggering loading screen
              setEmails(response.data.emails || []);
              setTotalEmails(response.data.total || 0);
              setUnreadCount(response.data.unread || 0);
              setHasMore(response.data.has_more || false);
              
              if (newEmailCount > 0) {
                toast.success(`${newEmailCount} new email(s) arrived`);
              }
            } catch (error) {
              console.error("Silent email update failed:", error);
            }
          } else if (viewType === "inbox") {
            // User is on another page - just update count
            setTotalEmails(inboxResponse.data.total);
            if (newEmailCount > 0) {
              toast.info(`${newEmailCount} new email(s) - showing on page 1`);
            }
          }
        }
        
        // Also update archived count
        const archivedResponse = await axios.get(
          `${API}/admin/actors/${actorId}/archived-emails?limit=0&skip=0`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTotalArchivedEmails(archivedResponse.data.total || 0);
        
      } catch (error) {
        // Silent fail for polling
        console.error("Polling error:", error);
      }
    }, 30000); // Poll every 30 seconds
    
    return () => clearInterval(pollInterval);
  }, [actorId, totalEmails, page, inboxSearch, viewType, loadingEmails]);

  const fetchActorDetails = async () => {
    try {
      setLoadingActor(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const foundActor = response.data.find(a => a.id === actorId);
      if (foundActor) {
        setActor(foundActor);
      } else {
        toast.error("AI Actor not found");
        navigate("/ai/actors");
      }
    } catch (error) {
      toast.error("Failed to fetch AI Actor details");
      console.error(error);
    } finally {
      setLoadingActor(false);
    }
  };

  const fetchTrainingStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/actors/${actorId}/email-analysis`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrainingStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch training status", error);
    }
  };

  const handleRetrain = async () => {
    if (!confirm("Retrain AI on all emails? This may take a minute.")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/actors/${actorId}/email-analysis/retrain`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("AI training started");
      fetchTrainingStatus();
    } catch (error) {
      console.error("Failed to start training", error);
      toast.error("Failed to start training");
    }
  };

  const fetchArchivedCount = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/actors/${actorId}/archived-emails?limit=0&skip=0`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTotalArchivedEmails(response.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch archived count", error);
    }
  };

  const fetchSentCount = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/actors/${actorId}/sent-emails?limit=0&skip=0`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTotalSentEmails(response.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch sent count", error);
    }
  };


  const fetchArchivedEmails = async (searchTerm = "", pageNum = 1) => {
    try {
      setLoadingEmails(true);
      const token = localStorage.getItem("token");
      const skip = (pageNum - 1) * EMAILS_PER_PAGE;
      
      const response = await axios.get(
        `${API}/admin/actors/${actorId}/archived-emails?limit=${EMAILS_PER_PAGE}&skip=${skip}${searchTerm ? `&search=${searchTerm}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setArchivedEmails(response.data.emails || []);
      setTotalArchivedEmails(response.data.total || 0);
      setHasMore(response.data.has_more || false);
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to fetch archived emails", error);
      toast.error("Failed to load archived emails");
    } finally {
      setLoadingEmails(false);
    }
  };


  const fetchSentEmails = async (searchTerm = "", pageNum = 1) => {
    try {
      setLoadingEmails(true);
      const token = localStorage.getItem("token");
      const skip = (pageNum - 1) * EMAILS_PER_PAGE;
      
      const response = await axios.get(
        `${API}/admin/actors/${actorId}/sent-emails?limit=${EMAILS_PER_PAGE}&skip=${skip}${searchTerm ? `&search=${searchTerm}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSentEmails(response.data.emails || []);
      setTotalSentEmails(response.data.total || 0);
      setHasMore(response.data.has_more || false);
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to fetch sent emails", error);
      toast.error("Failed to load sent emails");
    } finally {
      setLoadingEmails(false);
    }
  };


  const fetchEmails = async (searchTerm = "", pageNum = 1) => {
    try {
      setLoadingEmails(true);
      const token = localStorage.getItem("token");
      const skip = (pageNum - 1) * EMAILS_PER_PAGE;
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : "";
      const categoryParam = selectedCategory ? `&category=${encodeURIComponent(selectedCategory)}` : "";
      const tagsParam = selectedTags.length > 0 ? `&tags=${selectedTags.join(",")}` : "";
      const response = await axios.get(
        `${API}/admin/actors/${actorId}/emails?limit=${EMAILS_PER_PAGE}&skip=${skip}${searchParam}${categoryParam}${tagsParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Fetch response:', {
        total: response.data.total,
        count: response.data.count,
        unread: response.data.unread,
        has_more: response.data.has_more,
        skip: skip,
        pageNum: pageNum
      });
      
      setEmails(response.data.emails || []);
      setTotalEmails(response.data.total || 0);
      setUnreadCount(response.data.unread || 0); // Use total unread from backend
      setHasMore(response.data.has_more || false);
      setEmailsAccountCount(response.data.accounts_count || 0);
      setEmailAccounts(response.data.email_accounts || []);
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to fetch emails", error);
      toast.error("Failed to load emails");
    } finally {
      setLoadingEmails(false);
    }
  };

  const triggerSync = async () => {
    try {
      setSyncing(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/actors/${actorId}/emails/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Email sync started");
      // Refresh emails after sync
      setTimeout(() => {
        if (viewType === "inbox") {
          fetchEmails(inboxSearch);
        } else {
          fetchArchivedEmails(archivedSearch);
        }
      }, 2000);
    } catch (error) {
      console.error("Failed to trigger sync", error);
      toast.error("Failed to sync emails");
    } finally {
      setSyncing(false);
    }
  };

  const triggerEmailAnalysis = async () => {
    try {
      setAnalyzingEmails(true);
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/actors/${actorId}/analyze-emails`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email analysis started! This will categorize your emails.");
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        const status = await fetchTrainingStatus();
        if (status?.status === "completed") {
          clearInterval(pollInterval);
          setAnalyzingEmails(false);
          toast.success("Email analysis completed!");
          fetchEmails(inboxSearch, page); // Refresh to show categories
        }
      }, 3000);
      
      // Stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setAnalyzingEmails(false);
      }, 300000);
    } catch (error) {
      console.error("Failed to start email analysis", error);
      toast.error("Failed to start email analysis");
      setAnalyzingEmails(false);
    }
  };
  
  const updateEmailCategory = async (emailId, newCategory) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/ai-email/update-email-category`,
        {
          email_id: emailId,
          actor_id: actorId,
          category: newCategory
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || `Category updated to ${newCategory}`);
      
      // Update local state
      setEmails(emails.map(email => 
        email.id === emailId ? { ...email, ai_category: newCategory } : email
      ));
    } catch (error) {
      console.error("Failed to update category", error);
      toast.error("Failed to update category");
    }
  };

  const handleEmailSearch = (e) => {
    const value = e.target.value;
    setEmailSearch(value);
    
    // Save to appropriate search state based on current tab
    if (viewType === "inbox") {
      setInboxSearch(value);
    } else {
      setArchivedSearch(value);
    }
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      setPage(1);
      if (viewType === "inbox") {
        fetchEmails(value, 1);
      } else {
        fetchArchivedEmails(value, 1);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  };

  const nextPage = () => {
    console.log('Next clicked, current page:', page, 'hasMore:', hasMore);
    if (hasMore) {
      const newPage = page + 1;
      setSelectedEmail(null); // Clear selection when changing pages
      if (viewType === "inbox") {
        fetchEmails(inboxSearch, newPage);
      } else {
        fetchArchivedEmails(archivedSearch, newPage);
      }
    }
  };

  const prevPage = () => {
    console.log('Prev clicked, current page:', page);
    if (page > 1) {
      const newPage = page - 1;
      setSelectedEmail(null); // Clear selection when changing pages
      if (viewType === "inbox") {
        fetchEmails(inboxSearch, newPage);
      } else {
        fetchArchivedEmails(archivedSearch, newPage);
      }
    }
  };

  const deleteEmail = async (email) => {
    if (!window.confirm("Are you sure you want to delete this email? This action cannot be undone.")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/admin/actors/${actorId}/emails/${email.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email deleted successfully");
      
      // Update unread count if deleting an unread email (not applicable for sent emails)
      if (!email.is_read && viewType !== "sent") {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      // Update total count and remove from appropriate list
      if (viewType === "inbox") {
        setTotalEmails(prev => Math.max(0, prev - 1));
        // Remove from inbox list
        setEmails(emails.filter(e => e.id !== email.id));
      } else if (viewType === "archived") {
        setTotalArchivedEmails(prev => Math.max(0, prev - 1));
        // Remove from archived list
        setArchivedEmails(archivedEmails.filter(e => e.id !== email.id));
      } else if (viewType === "sent") {
        setTotalSentEmails(prev => Math.max(0, prev - 1));
        // Remove from sent list
        setSentEmails(sentEmails.filter(e => e.id !== email.id));
      }
      if (selectedEmail?.id === email.id) {
        setSelectedEmail(null);
      }
    } catch (error) {
      console.error("Failed to delete email", error);
      toast.error("Failed to delete email");
    }
  };

  // Fetch available filters
  const fetchAvailableFilters = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/actors/${actorId}/email-filters`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableCategories(response.data.ai_categories || []);
      setAvailableTags(response.data.custom_tags || []);
    } catch (error) {
      console.error("Failed to fetch filters", error);
    }
  };

  // Toggle email selection
  const toggleEmailSelection = (emailId) => {
    setSelectedEmailIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  // Toggle select all visible emails
  const toggleSelectAll = () => {
    const currentEmails = viewType === "inbox" ? emails : archivedEmails;
    if (selectedEmailIds.size === currentEmails.length) {
      // Deselect all
      setSelectedEmailIds(new Set());
    } else {
      // Select all visible
      setSelectedEmailIds(new Set(currentEmails.map(e => e.id)));
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedEmailIds(new Set());
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    const count = selectedEmailIds.size;
    if (count === 0) return;

    if (!window.confirm(
      `Delete ${count} selected email${count > 1 ? 's' : ''}? This will remove them from both the system and your email server (Gmail/Outlook). This action cannot be undone.`
    )) {
      return;
    }

    try {
      setDeletingBulk(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/actors/${actorId}/emails/bulk-delete`,
        { email_ids: Array.from(selectedEmailIds) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { deleted_from_db, deleted_from_server, total_requested } = response.data;
      
      toast.success(
        `${deleted_from_db} email(s) deleted from database` +
        (deleted_from_server > 0 ? ` and ${deleted_from_server} from server` : '')
      );

      // Update unread count for deleted unread emails (not applicable for sent emails)
      if (viewType !== "sent") {
        const currentEmails = viewType === "inbox" ? emails : archivedEmails;
        const deletedUnreadCount = currentEmails.filter(
          e => selectedEmailIds.has(e.id) && !e.is_read
        ).length;
        setUnreadCount(prev => Math.max(0, prev - deletedUnreadCount));
      }

      // Remove deleted emails from UI
      if (viewType === "inbox") {
        setEmails(emails.filter(e => !selectedEmailIds.has(e.id)));
        setTotalEmails(prev => Math.max(0, prev - deleted_from_db));
      } else if (viewType === "archived") {
        setArchivedEmails(archivedEmails.filter(e => !selectedEmailIds.has(e.id)));
        setTotalArchivedEmails(prev => Math.max(0, prev - deleted_from_db));
      } else if (viewType === "sent") {
        setSentEmails(sentEmails.filter(e => !selectedEmailIds.has(e.id)));
        setTotalSentEmails(prev => Math.max(0, prev - deleted_from_db));
      }

      // Clear selection
      clearSelection();

      // Close email panel if selected email was deleted
      if (selectedEmail && selectedEmailIds.has(selectedEmail.id)) {
        setSelectedEmail(null);
      }

    } catch (error) {
      console.error("Failed to bulk delete emails", error);
      toast.error(error.response?.data?.detail || "Failed to delete emails");
    } finally {
      setDeletingBulk(false);
    }
  };

  // Apply filters
  const applyFilters = () => {
    clearSelection(); // Clear selection when filters change
    fetchEmails(inboxSearch, 1);
  };


  const markEmailAsRead = async (email, markAsRead = true) => {
    // Don't mark sent emails as read/unread - they don't have read status
    if (viewType === "sent") {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API}/admin/actors/${actorId}/emails/${email.id}/read?mark_as_read=${markAsRead}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      if (viewType === "inbox") {
        setEmails(emails.map(e => 
          e.id === email.id ? { ...e, is_read: markAsRead } : e
        ));
      } else {
        setArchivedEmails(archivedEmails.map(e => 
          e.id === email.id ? { ...e, is_read: markAsRead } : e
        ));
      }
      
      if (selectedEmail?.id === email.id) {
        setSelectedEmail({ ...selectedEmail, is_read: markAsRead });
      }
      
      // Update unread count
      if (markAsRead && !email.is_read) {
        // Marking as read, and it was unread before → decrease count
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else if (!markAsRead && email.is_read) {
        // Marking as unread, and it was read before → increase count
        setUnreadCount(prev => prev + 1);
      }
      
      console.log(`Email marked as ${markAsRead ? 'read' : 'unread'}, new unread count:`, unreadCount);
    } catch (error) {
      console.error("Failed to mark email", error);
      toast.error("Failed to update email status");
    }
  };

  if (loadingActor) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!actor) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">AI Actor not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{actor.name} - Emails</h1>
            <div className="flex gap-3 items-center">
              <p className="text-sm text-muted-foreground">
                {emailsAccountCount > 0 
                  ? `${emailsAccountCount} email account${emailsAccountCount > 1 ? 's' : ''} connected`
                  : 'No email accounts configured'}
              </p>
              {totalEmails > 0 && (
                <>
                  <span className="text-slate-300">•</span>
                  <p className="text-sm font-semibold text-slate-700">
                    {totalEmails} total emails
                  </p>
                  {unreadCount > 0 && (
                    <>
                      <span className="text-slate-300">•</span>
                      <Badge variant="default" className="text-xs">
                        {unreadCount} unread
                      </Badge>
                    </>
                  )}
                </>
              )}
            </div>
            {/* Show sync status for each account */}
            {emailAccounts.length > 0 && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {emailAccounts.map((account) => (
                  <Badge key={account.id} variant="outline" className="text-xs">
                    {account.email_address}: 
                    {account.is_syncing ? (
                      <span className="text-blue-600 ml-1">Syncing...</span>
                    ) : account.last_synced_at ? (
                      <span className="text-green-600 ml-1">
                        Synced {new Date(account.last_synced_at).toLocaleTimeString()}
                      </span>
                    ) : (
                      <span className="text-orange-600 ml-1">Not synced yet</span>
                    )}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setChatOpen(!chatOpen)}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              AI Chat
            </Button>
            <Button
              variant="outline"
              onClick={triggerEmailAnalysis}
              disabled={analyzingEmails}
            >
              <Tag className={`w-4 h-4 mr-2 ${analyzingEmails ? 'animate-pulse' : ''}`} />
              {analyzingEmails ? 'Analyzing...' : 'Analyze Emails'}
            </Button>
            <Button
              onClick={triggerSync}
              disabled={syncing || emailAccounts.some(a => a.is_syncing)}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          </div>
        </div>
        
        {/* AI Chat Panel */}
        {chatOpen && (
          <EmailChatPanel 
            key={`chat-${actorId}-${chatOpen}`}
            actorId={actorId} 
            isOpen={chatOpen} 
            onClose={() => setChatOpen(false)} 
          />
        )}

        {/* Main Email Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Inbox className="w-5 h-5 text-blue-600" />
              Email Inbox
            </CardTitle>
            <CardDescription>
              View and manage emails from all configured email accounts
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Training Progress Banner */}
            {trainingStatus.status === "in_progress" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Brain className="w-5 h-5 text-blue-600 animate-pulse" />
                    <div>
                      <p className="font-medium text-blue-900">AI Training in Progress</p>
                      <p className="text-sm text-blue-700">
                        Analyzing emails for faster responses...
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600">
                      {trainingStatus.training_progress}%
                    </p>
                    <p className="text-xs text-blue-600">
                      {trainingStatus.analyzed_emails || 0} / {trainingStatus.total_emails} emails
                    </p>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${trainingStatus.training_progress}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Training Complete Badge */}
            {trainingStatus.status === "completed" && (
              <div className="flex items-center gap-2 text-sm text-green-600 mb-3 p-2 bg-green-50 rounded-lg">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">AI trained on {trainingStatus.total_emails} emails</span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-600">
                  Last updated: {trainingStatus.last_analyzed ? new Date(trainingStatus.last_analyzed).toLocaleString() : 'N/A'}
                </span>
                <button
                  onClick={handleRetrain}
                  className="ml-auto flex items-center gap-1 px-2 py-1 text-xs bg-white border border-green-300 rounded hover:bg-green-100 transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Retrain
                </button>
              </div>
            )}

            {/* View Type Tabs */}
            <div className="flex gap-2 mb-4 border-b">
              <button
                onClick={() => setViewType("inbox")}
                className={`px-4 py-2 font-medium transition-colors ${
                  viewType === "inbox"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Inbox className="w-4 h-4 inline mr-2" />
                Inbox ({totalEmails})
              </button>
              <button
                onClick={() => setViewType("archived")}
                className={`px-4 py-2 font-medium transition-colors ${
                  viewType === "archived"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Mail className="w-4 h-4 inline mr-2" />
                Archived ({totalArchivedEmails})
              </button>
              <button
                onClick={() => setViewType("sent")}
                className={`px-4 py-2 font-medium transition-colors ${
                  viewType === "sent"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <MailOpen className="w-4 h-4 inline mr-2" />
                Sent ({totalSentEmails})
              </button>
              <button
                onClick={() => navigate(`/ai/actors/${actorId}/ai-tasks`)}
                className="px-4 py-2 font-medium transition-colors text-gray-500 hover:text-gray-700"
              >
                <CheckSquare className="w-4 h-4 inline mr-2" />
                AI Tasks
              </button>
              <button
                onClick={() => navigate(`/ai/actors/${actorId}/contacts`)}
                className="px-4 py-2 font-medium transition-colors text-gray-500 hover:text-gray-700"
              >
                <User className="w-4 h-4 inline mr-2" />
                Contacts
              </button>
            </div>

            {!actor.email_configs || actor.email_configs.length === 0 ? (
              <div className="p-12 border-2 border-dashed rounded-lg bg-blue-50 text-center">
                <Inbox className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No Email Accounts Configured</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                  Configure email accounts in Edit AI Actor to view and manage emails here.
                </p>
                <Button onClick={() => navigate(`/ai/actors/edit/${actorId}`)} variant="outline">
                  <Settings className="w-4 h-4 mr-2" />
                  Configure Email Accounts
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Search, Pagination and Refresh Bar */}
                <div className="flex gap-2 items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search emails by subject or sender..."
                      value={emailSearch}
                      onChange={handleEmailSearch}
                      className="pl-10"
                    />
                  </div>
                  
                  {/* Pagination */}
                  {(viewType === "inbox" ? totalEmails : viewType === "archived" ? totalArchivedEmails : totalSentEmails) > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {(page - 1) * EMAILS_PER_PAGE + 1}-{Math.min(page * EMAILS_PER_PAGE, (viewType === "inbox" ? totalEmails : viewType === "archived" ? totalArchivedEmails : totalSentEmails))} of {viewType === "inbox" ? totalEmails : viewType === "archived" ? totalArchivedEmails : totalSentEmails}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={prevPage}
                        disabled={page === 1 || loadingEmails}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={nextPage}
                        disabled={!hasMore || loadingEmails}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                  
                  <Button
                    variant="outline"
                    onClick={triggerSync}
                    disabled={syncing || emailAccounts.some(a => a.is_syncing)}
                    title="Sync all email accounts now"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncing || emailAccounts.some(a => a.is_syncing) ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                {/* Filter Bar - Only show for inbox */}
                {viewType === "inbox" && (
                  <div className="flex gap-2 items-center flex-wrap">
                    <Select value={selectedCategory || "all"} onValueChange={(val) => setSelectedCategory(val === "all" ? "" : val)}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Filter by category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {availableCategories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category === 'Promotional' && '🛍️'} 
                            {category === 'Newsletter' && '📰'} 
                            {category === 'Personal' && '👤'} 
                            {category === 'Work' && '💼'} 
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select 
                      value={selectedTags.length > 0 ? selectedTags[0] : "all"} 
                      onValueChange={(value) => setSelectedTags(value === "all" ? [] : [value])}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Filter by tag" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Tags</SelectItem>
                        {availableTags.map((tag) => (
                          <SelectItem key={tag} value={tag}>
                            {tag}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Button 
                      variant="outline" 
                      onClick={applyFilters}
                      disabled={loadingEmails}
                    >
                      Apply Filters
                    </Button>

                    {(selectedCategory || selectedTags.length > 0) && (
                      <Button 
                        variant="ghost" 
                        onClick={() => {
                          setSelectedCategory("");
                          setSelectedTags([]);
                          applyFilters();
                        }}
                        className="text-red-600 hover:text-red-700"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                )}

                {/* Bulk Selection Toolbar - Only show for inbox when emails exist */}
                {viewType === "inbox" && emails.length > 0 && (
                  <div className="flex items-center justify-between bg-slate-50 border rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={toggleSelectAll}
                        className="flex items-center gap-2"
                      >
                        <CheckSquare className="w-4 h-4" />
                        {selectedEmailIds.size === emails.length ? 'Deselect All' : 'Select All'}
                      </Button>
                      
                      {selectedEmailIds.size > 0 && (
                        <span className="text-sm text-muted-foreground">
                          {selectedEmailIds.size} email{selectedEmailIds.size > 1 ? 's' : ''} selected
                        </span>
                      )}
                    </div>

                    {selectedEmailIds.size > 0 && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearSelection}
                        >
                          Clear Selection
                        </Button>
                        
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleBulkDelete}
                          disabled={deletingBulk}
                          className="flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          {deletingBulk ? 'Deleting...' : `Delete ${selectedEmailIds.size}`}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Loading State */}
                {loadingEmails ? (
                  <div className="text-center py-12">
                    <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Loading emails...</p>
                  </div>
                ) : (viewType === "inbox" ? emails : viewType === "archived" ? archivedEmails : sentEmails).length === 0 ? (
                  <div className="p-12 border-2 border-dashed rounded-lg bg-slate-50 text-center">
                    <Inbox className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      {emailSearch ? 'No Emails Found' : `Your ${viewType === "inbox" ? "Inbox" : viewType === "archived" ? "Archive" : "Sent"} is Empty`}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {emailSearch 
                        ? 'No emails found matching your search. Try a different search term.' 
                        : `No emails found in your ${viewType === "inbox" ? "inbox" : viewType === "archived" ? "archive" : "sent folder"}.`}
                    </p>
                  </div>
                ) : selectedEmail ? (
                  /* Full-Width Email Detail View */
                  <div className="border rounded-lg overflow-hidden">
                    {/* Back Button Header */}
                    <div className="bg-slate-50 border-b px-4 py-3 flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedEmail(null)}
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to {viewType === "inbox" ? "Inbox" : viewType === "archived" ? "Archive" : "Sent"}
                      </Button>
                    </div>
                    {/* Email Detail Content */}
                    <div className="p-6 overflow-y-auto flex-1">
                      {/* Email Header */}
                      <div className="mb-6 pb-6 border-b">
                        <div className="flex items-start justify-between gap-4 mb-4">
                          <h2 className="text-2xl font-bold text-slate-900">
                            {selectedEmail.subject || "(No Subject)"}
                          </h2>
                          <div className="flex gap-2 shrink-0">
                            {viewType === "inbox" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => markEmailAsRead(selectedEmail, !selectedEmail.is_read)}
                                title={selectedEmail.is_read ? "Mark as unread" : "Mark as read"}
                              >
                                {selectedEmail.is_read ? (
                                  <><MailOpen className="w-4 h-4 mr-2" /> Mark Unread</>
                                ) : (
                                  <><Mail className="w-4 h-4 mr-2" /> Mark Read</>
                                )}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteEmail(selectedEmail)}
                              className="text-red-600 hover:text-red-700"
                              title={viewType === "archived" ? "Delete permanently" : "Delete email"}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {viewType === "archived" ? "Delete Permanently" : "Delete"}
                            </Button>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-1">
                              <p className="text-sm"><strong>From:</strong> {selectedEmail.from_addr || selectedEmail.from}</p>
                              <p className="text-sm"><strong>To:</strong> {selectedEmail.to_addr || selectedEmail.to}</p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(selectedEmail.date).toLocaleString('en-US', {
                                  weekday: 'short',
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                          
                          {/* Tags and Category */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {selectedEmail.ai_category && (
                              <Select
                                value={selectedEmail.ai_category}
                                onValueChange={(value) => updateEmailCategory(selectedEmail.id, value)}
                              >
                                <SelectTrigger className="h-7 w-36 text-xs">
                                  <Badge 
                                    variant="outline"
                                    className="text-xs cursor-pointer"
                                    style={{
                                      backgroundColor: 
                                        selectedEmail.ai_category === 'Promotional' ? '#f59e0b' :
                                        selectedEmail.ai_category === 'Newsletter' ? '#3b82f6' :
                                        selectedEmail.ai_category === 'Personal' ? '#10b981' :
                                        '#8b5cf6',
                                      color: 'white'
                                    }}
                                  >
                                    <Tag className="w-3 h-3 mr-1" />
                                    {selectedEmail.ai_category}
                                  </Badge>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Promotional">🛍️ Promotional</SelectItem>
                                  <SelectItem value="Newsletter">📰 Newsletter</SelectItem>
                                  <SelectItem value="Personal">👤 Personal</SelectItem>
                                  <SelectItem value="Work">💼 Work</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            
                            <Badge variant="outline" className="text-xs">
                              Account: {selectedEmail.email_address}
                            </Badge>
                            
                            {selectedEmail.tags && selectedEmail.tags.length > 0 && (
                              <>
                                {selectedEmail.tags.map((tag, idx) => (
                                  <Badge 
                                    key={idx} 
                                    variant="default" 
                                    className="text-xs"
                                    style={{
                                      backgroundColor: tag.toLowerCase() === 'boss' ? '#ef4444' : tag.toLowerCase() === 'important' ? '#f59e0b' : '#3b82f6',
                                      color: 'white'
                                    }}
                                  >
                                    {tag}
                                  </Badge>
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Email Body */}
                      <div className="prose max-w-none">
                        {selectedEmail.content_type === 'html' ? (
                          <div 
                            className="text-sm text-slate-700 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: selectedEmail.html_body || selectedEmail.body }}
                          />
                        ) : (
                          <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans leading-relaxed">
                            {selectedEmail.text_body || selectedEmail.body || "(No content)"}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Full-Width Email List View */
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-50 border-b px-4 py-3">
                      <p className="text-sm font-semibold text-slate-700">
                        {viewType === "inbox" ? "Inbox" : viewType === "archived" ? "Archived" : "Sent"} {viewType === "inbox" && unreadCount > 0 && `(${unreadCount} unread)`}
                      </p>
                    </div>
                    <div className="divide-y">
                      {(viewType === "inbox" ? emails : viewType === "archived" ? archivedEmails : sentEmails).map((email) => (
                        <div
                          key={email.id}
                          className={`p-4 hover:bg-gray-50 transition-colors ${!email.is_read ? 'bg-blue-50/30' : ''} ${selectedEmailIds.has(email.id) ? 'bg-blue-100' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Checkbox for bulk selection - only show for inbox */}
                            {viewType === "inbox" && (
                              <input
                                type="checkbox"
                                checked={selectedEmailIds.has(email.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleEmailSelection(email.id);
                                }}
                                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                            )}
                            
                            {/* Email content - clickable area */}
                            <div 
                              className="flex-1 cursor-pointer"
                              onClick={() => {
                                setSelectedEmail(email);
                                if (!email.is_read && viewType !== "sent") {
                                  markEmailAsRead(email, true);
                                }
                              }}
                            >
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <p className={`text-sm truncate flex-1 ${!email.is_read ? 'font-bold' : 'font-medium'}`}>
                                  {viewType === "sent" ? (
                                    <span><span className="text-gray-500">To:</span> {email.to_addr || email.to}</span>
                                  ) : (
                                    email.from_addr || email.from
                                  )}
                                </p>
                                {!email.is_read && viewType !== "sent" && (
                                  <Badge variant="default" className="text-xs shrink-0">New</Badge>
                                )}
                              </div>
                            <p className={`text-sm truncate ${!email.is_read ? 'font-semibold' : ''}`}>
                              {email.subject || "(No Subject)"}
                            </p>
                            
                            {/* AI Category Badge with Edit Dropdown */}
                            <div className="flex gap-2 items-center mt-1">
                              {email.ai_category && (
                                <Select
                                  value={email.ai_category}
                                  onValueChange={(value) => updateEmailCategory(email.id, value)}
                                >
                                  <SelectTrigger className="h-6 w-32 text-xs border-none">
                                    <Badge 
                                      variant="outline"
                                      className="text-xs cursor-pointer"
                                      style={{
                                        backgroundColor: 
                                          email.ai_category === 'Promotional' ? '#f59e0b' :
                                          email.ai_category === 'Newsletter' ? '#3b82f6' :
                                          email.ai_category === 'Personal' ? '#10b981' :
                                          '#8b5cf6',
                                        color: 'white',
                                        fontSize: '10px',
                                        padding: '2px 6px'
                                      }}
                                    >
                                      <Tag className="w-3 h-3 mr-1" />
                                      {email.ai_category}
                                    </Badge>
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Promotional">🛍️ Promotional</SelectItem>
                                    <SelectItem value="Newsletter">📰 Newsletter</SelectItem>
                                    <SelectItem value="Personal">👤 Personal</SelectItem>
                                    <SelectItem value="Work">💼 Work</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                              
                              {/* Manual Tags */}
                              {email.tags && email.tags.length > 0 && (
                                <div className="flex gap-1 flex-wrap">
                                  {email.tags.map((tag, idx) => (
                                    <Badge 
                                      key={idx} 
                                      variant="default" 
                                      className="text-xs"
                                      style={{
                                        backgroundColor: tag.toLowerCase() === 'boss' ? '#ef4444' : tag.toLowerCase() === 'important' ? '#f59e0b' : '#3b82f6',
                                        color: 'white',
                                        fontSize: '10px',
                                        padding: '2px 6px'
                                      }}
                                    >
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2 max-h-10 overflow-hidden">
                              {(() => {
                                // Extract preview from email body (strip HTML tags and clean up)
                                // Priority: text_body > body > html_body
                                let body = "";
                                
                                if (email.text_body && email.text_body.trim().length > 10) {
                                  body = email.text_body;
                                } else if (email.body && email.body.trim().length > 10) {
                                  body = email.body;
                                } else if (email.html_body) {
                                  body = email.html_body;
                                }
                                
                                if (!body) return "(No content)";
                                
                                // Strip HTML tags and clean up whitespace
                                let strippedBody = body
                                  .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // Remove style tags
                                  .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') // Remove script tags
                                  .replace(/<!--[\s\S]*?-->/g, '') // Remove HTML comments
                                  .replace(/<[^>]*>/g, '') // Remove all HTML tags
                                  .replace(/&nbsp;/g, ' ') // Replace nbsp with space
                                  .replace(/&[#a-z0-9]+;/gi, ' ') // Remove HTML entities
                                  .replace(/\s+/g, ' ') // Collapse multiple spaces
                                  .replace(/\n/g, ' ') // Remove newlines
                                  .trim();
                                
                                const preview = strippedBody.slice(0, 150);
                                return preview || "(No preview available)";
                              })()}
                            </p>
                            <div className="flex items-center justify-between mt-2">
                              <p className="text-xs text-muted-foreground shrink-0">
                                {(() => {
                                  const emailDate = new Date(email.date);
                                  const today = new Date();
                                  const yesterday = new Date(today);
                                  yesterday.setDate(yesterday.getDate() - 1);
                                  
                                  if (emailDate.toDateString() === today.toDateString()) {
                                    return emailDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                                  } else if (emailDate.toDateString() === yesterday.toDateString()) {
                                    return 'Yesterday';
                                  } else if (emailDate.getFullYear() === today.getFullYear()) {
                                    return emailDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                  } else {
                                    return emailDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                                  }
                                })()}
                              </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
